import React from "react";
import { Row, Col, Button, FormGroup, FormControl } from "react-bootstrap";

import PR from "../../../assets/state-svg/puerto-rico";
import GU from "../../../assets/state-svg/guam";
import MP from "../../../assets/state-svg/marian";
import VI from "../../../assets/state-svg/us-virigin-island";
import DateInput from "../../common/DateInput";
/**
 * SourceLinking
 *
 * Props (all passed from parent — nothing invented):
 *   sourceTypes, filteredCategories, filteredCaseSources
 *   selectedType, selectedCategory, selectedSourceName
 *   handleTypeChange, handleCategoryChange, handleSourceNameChange
 *   disabledCategory, disabledSourceName, getFieldClass
 *   sourceTypeOpen, setSourceTypeOpen
 *   sourceCategoryOpen, setSourceCategoryOpen
 *   sourceNameOpen, setSourceNameOpen
 *   linkedSources     – [{ _key, type_name, category_name, source_name, source_type, source_category, source_name_id, location: {...} }]
 *   onLinkedChange    – (newArray) => void
 *
 *   // For state picker — same pattern as parent's homeStateSource
 *   statesData                – array from /api/states/
 *   setShowStatesModalSource  – fn  (parent opens the modal)
 *   homeStateSource           – currently selected state obj { StateAbr, ... }
 *   onStateSelectedForSource  – fn(stateObj, sourceKey) — parent calls this after modal closes
 */
const SourceLinking = ({
  sourceTypes,
  filteredCategories,
  filteredCaseSources,
  selectedType,
  selectedCategory,
  selectedSourceName,
  handleTypeChange,
  handleCategoryChange,
  handleSourceNameChange,
  disabledCategory,
  disabledSourceName,
  getFieldClass,
  sourceTypeOpen,
  setSourceTypeOpen,
  sourceCategoryOpen,
  setSourceCategoryOpen,
  sourceNameOpen,
  setSourceNameOpen,
  linkedSources = [],
  onLinkedChange,
  onOpenStateModal,
}) => {
  // ── link a new source ────────────────────────────────────────────────────────
  const handleLink = () => {
    if (!selectedSourceName || selectedSourceName === 0) return;

    const sourceObj = filteredCaseSources?.find((s) => s.id === selectedSourceName);
    const typeObj = sourceTypes?.find((t) => t.id === selectedType);
    const catObj = filteredCategories?.find((c) => c.id === selectedCategory);

    if (!sourceObj) return;
    if (linkedSources.some((l) => l.source_name_id === selectedSourceName)) return;

    onLinkedChange([
      ...linkedSources,
      {
        _key: Date.now(),
        source_type_id: selectedType || null,
        source_category_id: selectedCategory || null,
        case_source_id: selectedSourceName,
        metadata: {
          type_name: typeObj?.name ?? "",
          category_name: catObj?.name ?? "",
          source_name: sourceObj.name,
          source_date: "",
          source_time: "",
          source_address1: "",
          source_address2: "",
          source_city: "",
          metadatasource_state: "",
          source_zip: "",
          search_term: "",
        },
      },
    ]);

    handleTypeChange({ target: { value: 0 } });
    handleCategoryChange({ target: { value: 0 } });
    handleSourceNameChange({ target: { value: 0 } });
  };

  // ── remove a linked source ───────────────────────────────────────────────────
  const handleRemove = (key) => onLinkedChange(linkedSources.filter((l) => l._key !== key));

  // ── update a location field for a specific source ────────────────────────────
  const updateLocation = (key, field, val) =>
    onLinkedChange(
      linkedSources.map((l) =>
        l._key === key ? { ...l, metadata: { ...l.metadata, [field]: val } } : l
      )
    );

  // ── open state modal for a specific source ───────────────────────────────────

  const stateMap = {
    PR: PR,
    GU: GU,
    VI: VI,
    MP: MP,
  };
  const specialStates = Object.keys(stateMap);

  return (
    <div>
      {/* ── Linked sources — header row + location fields ───────────────────── */}
      {linkedSources.map((linked, idx) => (
        <div key={linked._key} style={{ marginBottom: "6px" }}>
          {/* Source N header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "25px",
              background: "var(--primary)",
              borderLeft: "3px solid var(--primary-80)",
              paddingLeft: "8px",
              paddingRight: "4px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0" }}>
              {/* Number badge */}
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "white",
                  marginRight: "8px",
                  whiteSpace: "nowrap",
                }}
              >
                Source {idx + 1}
              </span>

              <span style={{ color: "rgba(255,255,255,0.4)", marginRight: "8px" }}>|</span>

              {/* Type */}
              <span
                style={{ fontSize: "12px", fontWeight: "600", color: "white", marginRight: "4px" }}
              >
                {linked.metadata?.type_name || "—"}
              </span>

              {/* Category */}
              {linked.metadata?.category_name && (
                <>
                  <span style={{ color: "rgba(255,255,255,0.4)", margin: "0 5px" }}>›</span>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.85)",
                      marginRight: "4px",
                    }}
                  >
                    {linked.metadata?.category_name}
                  </span>
                </>
              )}

              {/* Name */}
              <span style={{ color: "rgba(255,255,255,0.4)", margin: "0 5px" }}>›</span>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "white" }}>
                {linked.metadata?.source_name}
              </span>
            </div>

            {/* Remove × */}
            <button
              onClick={() => handleRemove(linked._key)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "rgba(255,255,255,0.7)",
                fontSize: "18px",
                lineHeight: "1",
                padding: "0 6px",
              }}
              title="Remove source"
            >
              ×
            </button>
          </div>

          {/* Client Location label */}
          <div
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: "#555",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
              padding: "3px 0 2px 2px",
            }}
          >
            Client Location for Source {idx + 1}
          </div>

          {/* Date + Time row */}
          <Row className="mx-0 m-b-5">
            <Col sm="2" className="ps-0 p-r-5">
              <FormGroup className="m-0">
                <DateInput
                  placeholder="Source Date"
                  value={linked.metadata.source_date}
                  onChange={(e) => updateLocation(linked._key, "source_date", e.target.value)}
                  className="form-control height-25 p-l-5 p-0"
                />
              </FormGroup>
            </Col>

            <Col sm="2" className="ps-0 p-r-5">
              <FormGroup className="m-0">
                <FormControl
                  type={linked.metadata.source_time ? "time" : "text"}
                  onFocus={(e) => (e.target.type = "time")}
                  onBlur={(e) => {
                    if (!e.target.value) e.target.type = "text";
                  }}
                  placeholder="Source Time"
                  value={linked.metadata.source_time}
                  onChange={(e) => updateLocation(linked._key, "source_time", e.target.value)}
                  className="form-control height-25 p-l-5 p-0"
                />
              </FormGroup>
            </Col>

            <Col sm="8" className="ps-0 p-r-5">
              <FormGroup className="m-0">
                <FormControl
                  type="text"
                  placeholder="Enter Search Terms"
                  value={linked.metadata?.search_term}
                  onChange={(e) => updateLocation(linked._key, "search_term", e.target.value)}
                  className="form-control height-25 p-0 p-l-5"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Address row */}
          <Row className="mx-0 m-b-5">
            <Col sm="4" className="ps-0 p-r-5">
              <FormGroup className="m-0">
                <FormControl
                  type="text"
                  placeholder="Client Location Address 1"
                  value={linked.metadata.source_address1}
                  onChange={(e) => updateLocation(linked._key, "source_address1", e.target.value)}
                  className="form-control height-25 p-0 p-l-5"
                />
              </FormGroup>
            </Col>

            <Col sm="3" className="ps-0 p-r-5">
              <FormGroup className="m-0">
                <FormControl
                  type="text"
                  placeholder="Client Location Address 2"
                  value={linked.metadata.source_address2}
                  onChange={(e) => updateLocation(linked._key, "source_address2", e.target.value)}
                  className="form-control height-25 p-0 p-l-5"
                />
              </FormGroup>
            </Col>

            <Col className="ps-0 p-r-5">
              <FormGroup className="m-0">
                <FormControl
                  type="text"
                  placeholder="City"
                  value={linked.metadata.source_city}
                  onChange={(e) => updateLocation(linked._key, "source_city", e.target.value)}
                  className="form-control height-25 p-0 p-l-5"
                />
              </FormGroup>
            </Col>

            {/* State picker — opens parent's SelectStateModal */}
            <Col sm="1" style={{ paddingLeft: "0px", paddingRight: "5px", minWidth: "90px" }}>
              <FormGroup className="m-0">
                <div className="tabclient custom-select-new-provider">
                  <div
                    className={`dropdown-button rounded-0 height-25 p-l-5 p-0 form-control d-flex align-items-center ${linked.metadata.source_state ? "has-value" : ""}`}
                    onClick={() => onOpenStateModal(linked._key)}
                    style={{ cursor: "pointer" }}
                  >
                    <span>
                      {linked?.metadata?.source_state ? (
                        specialStates.includes(linked.metadata.source_state) ? (
                          <div className="d-flex align-items-center">
                            {React.createElement(stateMap[linked.metadata.source_state])}
                            <span className="m-l-5">{linked.metadata.source_state}</span>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center">
                            <svg
                              style={{
                                width: "15px",
                                height: "15px",
                                fill: "var(--primary-80)",
                                color: "var(--primary-80)",
                                stroke: "var(--primary-80)",
                              }}
                              className={`icon icon-state-${linked.metadata.source_state}`}
                            >
                              <use xlinkHref={`#icon-state-${linked.metadata.source_state}`} />
                            </svg>
                            <span className="m-l-5">{linked.metadata.source_state}</span>
                          </div>
                        )
                      ) : (
                        "State"
                      )}
                    </span>
                  </div>
                </div>
              </FormGroup>
            </Col>

            <Col sm="1" className="ps-0 p-r-5">
              <FormGroup className="m-0">
                <FormControl
                  type="text"
                  placeholder="ZIP"
                  value={linked.metadata.source_zip}
                  onChange={(e) => updateLocation(linked._key, "source_zip", e.target.value)}
                  className="form-control height-25 p-0 p-l-5"
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
      ))}

      {/* ── Add new source row ──────────────────────────────────────────────── */}
      <Row className="mx-0 m-b-5 m-t-5">
        {/* Source Type */}
        <div
          className="col-md-3 ps-0 p-r-5 custom-select-new-provider"
          style={{ position: "relative" }}
        >
          <div
            className={`dropdown-button form-control p-0 p-l-5 rounded-0 height-25 d-flex align-items-center ${getFieldClass(selectedType, false)}`}
            onClick={() => setSourceTypeOpen((prev) => !prev)}
            style={{ cursor: "pointer" }}
          >
            <span>
              {selectedType && selectedType !== 0
                ? sourceTypes?.find((t) => t.id === selectedType)?.name
                : "Select Source Type"}
            </span>
          </div>
          {sourceTypeOpen && (
            <div
              className="dropdown-menu p-0 mt-0"
              style={{
                width: "99%",
                border: "1px solid #ced4da",
                maxHeight: "200px",
                overflowY: "auto",
                scrollbarWidth: "none",
              }}
            >
              <div
                className="dropdown-option height-25 d-flex align-items-center"
                style={{ cursor: "pointer", paddingLeft: "5px" }}
                onClick={() => {
                  handleTypeChange({ target: { value: 0 } });
                  setSourceTypeOpen(false);
                }}
              >
                Select Source Type
              </div>
              {sourceTypes?.map((type) => (
                <div
                  key={type.id}
                  className="dropdown-option height-25 d-flex align-items-center"
                  style={{ cursor: "pointer", paddingLeft: "5px", fontWeight: "600" }}
                  onClick={() => {
                    handleTypeChange({ target: { value: type.id } });
                    setSourceTypeOpen(false);
                  }}
                >
                  {type.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Source Category */}
        <div
          className="col-md-3 ps-0 p-r-5 custom-select-new-provider"
          style={{ position: "relative" }}
        >
          <div
            className={`dropdown-button form-control p-0 p-l-5 rounded-0 height-25 d-flex align-items-center ${getFieldClass(selectedCategory, false)}`}
            onClick={() => {
              if (!disabledCategory) setSourceCategoryOpen((prev) => !prev);
            }}
            style={{
              cursor: disabledCategory ? "not-allowed" : "pointer",
              opacity: disabledCategory ? 0.6 : 1,
            }}
          >
            <span>
              {selectedCategory && selectedCategory !== 0
                ? filteredCategories?.find((c) => c.id === selectedCategory)?.name
                : "Select Source Category"}
            </span>
          </div>
          {sourceCategoryOpen && !disabledCategory && (
            <div
              className="dropdown-menu p-0 mt-0"
              style={{
                width: "99%",
                border: "1px solid #ced4da",
                maxHeight: "200px",
                overflowY: "auto",
                scrollbarWidth: "none",
              }}
            >
              <div
                className="dropdown-option height-25 d-flex align-items-center"
                style={{ cursor: "pointer", paddingLeft: "5px" }}
                onClick={() => {
                  handleCategoryChange({ target: { value: 0 } });
                  setSourceCategoryOpen(false);
                }}
              >
                Select Source Category
              </div>
              {filteredCategories?.map((category) => (
                <div
                  key={category.id}
                  className="dropdown-option height-25 d-flex align-items-center"
                  style={{ cursor: "pointer", paddingLeft: "5px", fontWeight: "600" }}
                  onClick={() => {
                    handleCategoryChange({ target: { value: category.id } });
                    setSourceCategoryOpen(false);
                  }}
                >
                  {category.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Source Name */}
        <div
          className="col-md-3 ps-0 p-r-5 custom-select-new-provider"
          style={{ position: "relative" }}
        >
          <div
            className={`dropdown-button form-control p-0 p-l-5 rounded-0 height-25 d-flex align-items-center ${getFieldClass(selectedSourceName, false)}`}
            onClick={() => {
              if (!disabledSourceName) setSourceNameOpen((prev) => !prev);
            }}
            style={{
              cursor: disabledSourceName ? "not-allowed" : "pointer",
              opacity: disabledSourceName ? 0.6 : 1,
            }}
          >
            <span>
              {selectedSourceName && selectedSourceName !== 0
                ? filteredCaseSources?.find((s) => s.id === selectedSourceName)?.name
                : "Select Source Name"}
            </span>
          </div>
          {sourceNameOpen && !disabledSourceName && (
            <div
              className="dropdown-menu p-0 mt-0"
              style={{
                width: "99%",
                border: "1px solid #ced4da",
                maxHeight: "200px",
                overflowY: "auto",
                scrollbarWidth: "none",
              }}
            >
              <div
                className="dropdown-option height-25 d-flex align-items-center"
                style={{ cursor: "pointer", paddingLeft: "5px" }}
                onClick={() => {
                  handleSourceNameChange({ target: { value: 0 } });
                  setSourceNameOpen(false);
                }}
              >
                Select Source Name
              </div>
              {filteredCaseSources?.map((source) => (
                <div
                  key={source.id}
                  className="dropdown-option height-25 d-flex align-items-center"
                  style={{ cursor: "pointer", paddingLeft: "5px", fontWeight: "600" }}
                  onClick={() => {
                    handleSourceNameChange({ target: { value: source.id } });
                    setSourceNameOpen(false);
                  }}
                >
                  {source.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Link button */}
        <Col sm="3" className="ps-0 p-r-5">
          <FormGroup className="m-0">
            <Button
              variant="success"
              disabled={!selectedSourceName || selectedSourceName === 0}
              onClick={handleLink}
              className="height-25 w-100 d-flex align-items-center justify-content-center"
              style={{ fontSize: "14px", fontWeight: "600" }}
            >
              Link Source to Case
            </Button>
          </FormGroup>
        </Col>
      </Row>
    </div>
  );
};

export default SourceLinking;
